import { memo, useCallback, useState, type JSX } from 'react'

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import { useColumns, RowActions, NewDialogPanelContent } from 'domains/ContentStores/ManagedStores'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { managedStoresOverviewRoute } from 'pages/NWPlatform/ContentStores/ManagedStores'
import { managedStoreConfigRoute } from 'pages/NWPlatform/ContentStores/ManagedStores/ManagedStore/Routing'
import { managedStoreQueryKeys } from 'services/queryKeys'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const navigate = useNavigate({ from: managedStoresOverviewRoute.fullPath })
  const { environmentId } = managedStoresOverviewRoute.useParams()
  const { query } = managedStoresOverviewRoute.useSearch()

  const [newDialogOpen, setNewDialogOpen] = useState(false)

  const managedStoresQuery = useInfiniteQuery({
    ...managedStoreQueryKeys.list({ environmentId, query }),
    select: (data) => data.pages.flatMap((page) => page.items),
  })

  const columns = useColumns()

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:Integration.searchManagedStores')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(
            () => queryClient.invalidateQueries({ queryKey: managedStoreQueryKeys.list({ environmentId }).queryKey }),

            [environmentId, queryClient],
          )}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        data={managedStoresQuery.data ?? []}
        columns={columns}
        isInitialLoadingGlobal={managedStoresQuery.isLoading}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={managedStoresOverviewRoute.fullPath}
            to={managedStoreConfigRoute.to}
            params={{ managedStoreId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('domain:Integration.managedStore', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} />}
      />

      <Dialog
        open={newDialogOpen}
        onClose={() => {
          setNewDialogOpen(false)
        }}
      >
        <NewDialogPanelContent />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
